<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import { useArrowsAiResources } from 'dashboard/composables/useArrowsAiResources';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PageLayout from 'dashboard/components-next/arrowsAi/PageLayout.vue';
import EmptyState from 'dashboard/components-next/arrowsAi/EmptyState.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';
import BulkSelectBar from 'dashboard/components-next/arrowsAi/assistant/BulkSelectBar.vue';
import ResourceCard from 'dashboard/components-next/arrowsAi/assistant/ResourceCard.vue';
import ResourceDropzone from 'dashboard/components-next/arrowsAi/assistant/ResourceDropzone.vue';
import ResourceDialog from 'dashboard/components-next/arrowsAi/assistant/ResourceDialog.vue';

const { t } = useI18n();
const route = useRoute();
const { getAssistant, rememberAssistant } = useArrowsAiAssistants();
const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() => {
  const current = getAssistant(assistantId.value);
  if (current) rememberAssistant(current.id);
  return current;
});
const { resources, isLoading, addResources, updateResource, deleteResources } =
  useArrowsAiResources(assistantId);
const selectedIds = ref([]);
const selectedLabel = computed(() =>
  t('ARROWS_AI.RESOURCES.SELECTED', { count: selectedIds.value.length })
);
const uploadDialogRef = ref(null);
const editDialogRef = ref(null);
const selectedResource = ref({});
const isEmpty = computed(() =>
  Boolean(assistant.value && !isLoading.value && !resources.value.length)
);
const ITEMS_PER_PAGE = 25;
const currentPage = ref(1);
const pagedResources = computed(() => {
  const start = (currentPage.value - 1) * ITEMS_PER_PAGE;
  return resources.value.slice(start, start + ITEMS_PER_PAGE);
});

watch(
  () => resources.value.length,
  total => {
    const maxPage = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
    if (currentPage.value > maxPage) currentPage.value = maxPage;
  }
);

const openUpload = () => uploadDialogRef.value.open();

const addedNames = files => {
  const names = files.map(file => file.name);
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} +${names.length - 3}`;
};

const handleAdd = async files => {
  try {
    await addResources(files);
    uploadDialogRef.value.close();
    await nextTick();
    useAlert(t('ARROWS_AI.RESOURCES.SUCCESS', { name: addedNames(files) }));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.RESOURCES.SAVE_ERROR'));
  }
};

const openEdit = resource => {
  selectedResource.value = resource;
  nextTick(() => editDialogRef.value.dialogRef.open());
};

const handleUpdate = async payload => {
  if (!payload.name.trim()) {
    useAlert(t('ARROWS_AI.RESOURCES.ERROR'));
    return;
  }

  try {
    await updateResource(selectedResource.value.id, {
      name: payload.name.trim(),
      description: payload.description.trim(),
    });
    editDialogRef.value.dialogRef.close();
    await nextTick();
    useAlert(t('ARROWS_AI.RESOURCES.UPDATED', { name: payload.name.trim() }));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.RESOURCES.SAVE_ERROR'));
  }
};

const toggleSelect = id => {
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter(item => item !== id)
    : [...selectedIds.value, id];
};

const removeResources = async ids => {
  try {
    await deleteResources(ids);
    selectedIds.value = selectedIds.value.filter(id => !ids.includes(id));
    useAlert(t('ARROWS_AI.RESOURCES.DELETED'));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.RESOURCES.DELETE_ERROR'));
  }
};
</script>

<template>
  <PageLayout
    :header-title="t('ARROWS_AI.RESOURCES.TITLE')"
    :button-label="t('ARROWS_AI.RESOURCES.ADD')"
    :is-empty="isEmpty"
    :show-pagination-footer="Boolean(assistant)"
    :current-page="currentPage"
    :total-count="resources.length"
    :items-per-page="ITEMS_PER_PAGE"
    @click="openUpload"
    @update:current-page="currentPage = $event"
  >
    <template #emptyState>
      <EmptyState
        :title="t('ARROWS_AI.RESOURCES.EMPTY_TITLE')"
        :subtitle="t('ARROWS_AI.RESOURCES.EMPTY_SUBTITLE')"
        :action-label="t('ARROWS_AI.RESOURCES.ADD')"
        @click="openUpload"
      />
    </template>
    <MissingAssistant v-if="!assistant" />
    <div v-else class="flex flex-col gap-4">
      <BulkSelectBar
        v-model="selectedIds"
        :items="pagedResources"
        :select-all-label="t('ARROWS_AI.RESOURCES.SELECT_ALL')"
        :unselect-all-label="t('ARROWS_AI.RESOURCES.UNSELECT_ALL')"
        :selected-label="selectedLabel"
        :delete-label="t('ARROWS_AI.RESOURCES.DELETE')"
        @bulk-delete="removeResources(selectedIds)"
      />
      <ResourceCard
        v-for="resource in pagedResources"
        :key="resource.id"
        :resource="resource"
        :selected="selectedIds.includes(resource.id)"
        @select="toggleSelect"
        @edit="openEdit"
        @delete="removeResources([$event])"
      />
    </div>
  </PageLayout>
  <Dialog
    ref="uploadDialogRef"
    type="edit"
    :title="t('ARROWS_AI.RESOURCES.ADD')"
    :description="t('ARROWS_AI.RESOURCES.ADD_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <ResourceDropzone @add="handleAdd" />
    <template #footer />
  </Dialog>
  <ResourceDialog
    ref="editDialogRef"
    :resource="selectedResource"
    @submit="handleUpdate"
  />
</template>
